/* eslint-disable no-debugger */
import { useEffect, useState, useCallback } from 'react';
import useEmbeddedKendoReportData from '../../../state/embeddedKendoReport/UseEmbeddedKendoReportData';

/**
 * Generic hook for fetching Kendo report data
 * Can be used by any report component that needs to fetch data from the Kendo reporting API
 * 
 * @param {number} reportInfoId - The ID of the report to fetch
 * @param {object} reportParameters - Parameters to pass to the report
 * @param {object} options - Optional configuration
 * @param {boolean} options.autoFetch - Whether to automatically fetch data when reportInfoId changes (default: true)
 * @param {function} options.onSuccess - Callback function called when data is successfully fetched
 * @param {function} options.onError - Callback function called when an error occurs
 * @param {function} options.dataTransform - Function to transform the data before setting it in state
 * @returns {object} { reportData, isLoaded, isLoading, error, refetch }
 */
const useKendoReportData = (reportInfoId, reportParameters, options = {}) => {
  const {
    autoFetch = true,
    onSuccess,
    onError,
    dataTransform
  } = options;

  const INITIAL_STATE = {
    reportData: [],
    isLoaded: false,
    isLoading: false,
    error: null
  };

  const [state, setState] = useState({ ...INITIAL_STATE });
  const { getKendoReportData } = useEmbeddedKendoReportData();

  const fetchReportData = useCallback(async () => {
    if (!reportInfoId || reportInfoId <= 0) {
      setState(prev => ({
        ...prev,
        isLoaded: true,
        isLoading: false,
        reportData: [],
        error: null
      }));
      return;
    }

    setState(prev => ({
      ...prev,
      isLoading: true,
      isLoaded: false,
      error: null,
      reportData: []
    }));

    try {
      const reportPromise = getKendoReportData(reportInfoId, reportParameters);

      if (reportPromise) {
        const result = await reportPromise;
        let reportData = result.objData || [];

        // Apply data transformation if provided
        if (dataTransform && typeof dataTransform === 'function') {
          reportData = dataTransform(reportData);
        }

        // Scan for date field strings and convert to Date objects so grid sorting works correctly
        reportData = reportData.map(item => {
          const newItem = { ...item };
          Object.keys(newItem).forEach(key => {
            if (
              typeof newItem[key] === 'string' &&
              (
                // ISO date format
                /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|([+-]\d{2}:\d{2}))?)?$/.test(newItem[key]) ||
                // mm/dd/yyyy format
                /^\d{2}\/\d{2}\/\d{4}$/.test(newItem[key])
              )
            ) {
              const date = new Date(newItem[key]);
              if (!isNaN(date.getTime())) {
                newItem[key] = date;
              }
            }
          });
          return newItem;
        });

        setState(prev => ({
          ...prev,
          isLoaded: true,
          isLoading: false,
          reportData: reportData,
          error: null
        }));

        // Call success callback if provided
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(reportData, result);
        }
      } else {
        setState(prev => ({
          ...prev,
          isLoaded: true,
          isLoading: false,
          reportData: [],
          error: 'No report promise returned'
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoaded: true,
        isLoading: false,
        reportData: [],
        error: error.message || 'Failed to fetch report data'
      }));

      // Call error callback if provided
      if (onError && typeof onError === 'function') {
        onError(error);
      }
    }
  }, [reportInfoId, reportParameters, getKendoReportData, dataTransform, onSuccess, onError]);

  useEffect(() => {
    fetchReportData();
  }, [reportParameters]);

  // Manual refetch function
  const refetch = fetchReportData;

  return {
    ...state,
    refetch
  };
};

export default useKendoReportData;
